import { FC, useState } from "react";
import { useInstallPrompt } from "../../../shared/lib/pwa";
import { InstallHelpSheet } from "./InstallHelpSheet";

type Props = {
  /** Called after the entry has done its job, so a drawer can close itself. */
  onDone?: () => void;
  className?: string;
};

/**
 * The permanent way to install, for anyone who dismissed the banner or never saw it.
 *
 * Renders nothing when the app is already installed. On every other platform it always
 * renders: with a captured prompt the click fires Chrome's dialog, without one it opens the
 * platform-worded instructions — the same fallback logic as the banner.
 */
export const InstallAppEntry: FC<Props> = ({ onDone, className }) => {
  const { canPrompt, isStandalone, promptInstall } = useInstallPrompt();
  const [showHelpSheet, setShowHelpSheet] = useState(false);

  if (isStandalone) return null;

  const handleClick = async () => {
    if (canPrompt) {
      await promptInstall();
      onDone?.();
      return;
    }
    setShowHelpSheet(true);
    // Close the drawer right away: the sheet is portaled to <body>, so it does not need the
    // drawer alive — and instructions under a 288px overlay would be unreadable.
    onDone?.();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={className ?? "w-full py-1 text-left text-base text-[#124093] hover:underline"}
      >
        App installieren
      </button>
      <InstallHelpSheet show={showHelpSheet} setShow={setShowHelpSheet} />
    </>
  );
};
